/**
 * cg-subnav.js
 *
 * Sticky merchant-curated section nav for the Cow Guys Farm Shopify theme.
 *
 * - <li> items are rendered server-side from `nav_item` blocks (Liquid).
 * - For each link whose href starts with `#`, find the in-page target and
 *   wire it up for scroll-spy with IntersectionObserver + a gumshoe-style
 *   reverse-iterate fallback and an `isAtBottom()` override so the last
 *   tracked section always activates.
 * - External / page links (non-`#`) are left alone — no scroll-spy.
 * - Toggler: tap toggles `cg-subnav--expanded` + aria-expanded. Tapping a
 *   link while expanded collapses the bar.
 *
 * No external deps.
 */
(function () {
  'use strict';

  var SELECTOR = '[data-cg-subnav]';
  var ANCHOR_TARGET_ATTR = 'data-cg-subnav-anchor-target';

  function getHeaderHeight() {
    try {
      var raw = getComputedStyle(document.documentElement)
        .getPropertyValue('--header-height');
      var n = parseFloat(raw);
      if (!isNaN(n) && n > 0) return n;
    } catch (e) {}
    return 192;
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function escapeAttr(s) {
    return String(s).replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  // Copy helper — falls back to a hidden textarea + execCommand because the
  // Shopify theme editor's iframe usually blocks navigator.clipboard.writeText.
  function copyText(text) {
    var ok = false;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      try {
        navigator.clipboard.writeText(text);
        ok = true;
      } catch (e) {}
    }
    if (!ok) {
      var ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.top = '-1000px';
      ta.style.left = '-1000px';
      document.body.appendChild(ta);
      var prevSel = document.getSelection().rangeCount ? document.getSelection().getRangeAt(0) : null;
      ta.select();
      ta.setSelectionRange(0, text.length);
      try { ok = document.execCommand('copy'); } catch (e) {}
      document.body.removeChild(ta);
      if (prevSel) {
        document.getSelection().removeAllRanges();
        document.getSelection().addRange(prevSel);
      }
    }
    return ok;
  }

  class CgSubnav {
    constructor(el) {
      this.el = el;
      this.inner = el.querySelector('[data-cg-subnav-inner]');
      this.list = el.querySelector('[data-cg-subnav-list]');
      this.toggler = el.querySelector('[data-cg-subnav-toggler]');

      this.headerHeight = getHeaderHeight();
      this.items = [];           // [{ li, a, target, anchor }]
      this.observer = null;
      this.scrollHandler = null;
      this.rafToken = null;
      this.activeEl = null;
      this.togglerHandler = null;
      this.linkHandlers = [];
      this.lockedTarget = null;
      this.lockedUntil = 0;
      this.stickyHeaderEl = null;
      this.stickyTrackHandler = null;
      this.stickyRafActive = false;
      this.stickyLastScrollAt = 0;

      // Theme editor only: render a "detected sections on this page" helper
      // panel above the bar. Gated on the merchant's "Show editor helper
      // panel" checkbox so it doesn't always clutter the editor preview.
      if (window.Shopify && window.Shopify.designMode &&
          el.getAttribute('data-cg-subnav-show-helper') === 'true') {
        this.initEditorHelper();
      }

      this.links = Array.from(el.querySelectorAll('.cg-subnav__link'));
      this.mapLinksToTargets();

      // Scroll-spy assumes items are ordered by their targets' vertical
      // position on the page, not by pill order. Sort so reverse-iterate
      // fallback picks the right active item regardless of pill order.
      this.items.sort(function (a, b) {
        return a.target.getBoundingClientRect().top
             - b.target.getBoundingClientRect().top;
      });

      this.el.classList.add('cg-subnav--ready');

      if (this.items.length > 0) {
        this.initScrollSpy();
      }
      this.initToggler();
      this.initClickCollapse();
      this.initStickyTracking();
    }

    // Track the Dawn <sticky-header> element so the subnav slides up to take
    // the header's place when it hides on scroll-down, and slides back down
    // when the header reappears on scroll-up.
    initStickyTracking() {
      var self = this;
      this.stickyHeaderEl = document.querySelector('sticky-header');
      if (!this.stickyHeaderEl) return;

      var tick = function () {
        self.stickyRafActive = false;
        if (!self.stickyHeaderEl) return;
        var rect = self.stickyHeaderEl.getBoundingClientRect();
        // rect.bottom reflects the header's current transform — when sliding
        // out it goes from headerHeight down to 0 (or negative). Floor at 8px.
        var top = Math.max(8, rect.bottom + 8);
        self.el.style.top = top + 'px';
        // Keep ticking for ~500ms after last scroll so we follow the header's
        // CSS transition even when scroll events have stopped firing.
        if (Date.now() - self.stickyLastScrollAt < 500) {
          self.stickyRafActive = true;
          requestAnimationFrame(tick);
        }
      };

      this.stickyTrackHandler = function () {
        self.stickyLastScrollAt = Date.now();
        if (!self.stickyRafActive) {
          self.stickyRafActive = true;
          requestAnimationFrame(tick);
        }
      };

      window.addEventListener('scroll', this.stickyTrackHandler, { passive: true });
      window.addEventListener('resize', this.stickyTrackHandler, { passive: true });
      tick(); // seed initial top
    }

    // ------------------------------------------------------------------
    // Editor helper (Shopify.designMode only)
    // Scans the page for candidate sections and renders a panel of cards
    // with copy buttons, so the merchant can paste label + anchor into
    // Manual-mode Nav item blocks without guessing.
    // ------------------------------------------------------------------
    initEditorHelper() {
      var detected = this.scanCandidates();
      var panel = document.createElement('div');
      panel.className = 'cg-subnav-helper';
      panel.setAttribute('data-cg-subnav-helper', '');

      var html = '';
      html += '<div class="cg-subnav-helper__head">';
      html += '<strong>Subnav helper</strong> &mdash; detected ' + detected.length + ' section' + (detected.length === 1 ? '' : 's') + ' on this page. ';
      html += 'Add a <kbd>Nav item</kbd> block in the sidebar, then click a field below to select it and press <kbd>Ctrl/Cmd+C</kbd> to copy.';
      html += '</div>';

      if (detected.length === 0) {
        html += '<div class="cg-subnav-helper__empty">No sections found on this page. Sections need an h1 or h2 heading and at least 200px of height to be detected.</div>';
      } else {
        html += '<ul class="cg-subnav-helper__list">';
        detected.forEach(function (item) {
          html += '<li class="cg-subnav-helper__row">';
          html += '<span class="cg-subnav-helper__field-label">Label</span>';
          html += '<input type="text" class="cg-subnav-helper__field" readonly value="' + escapeAttr(item.label) + '" data-tip="Click to select, Ctrl/Cmd+C to copy" />';
          html += '<span class="cg-subnav-helper__field-label">Link</span>';
          html += '<input type="text" class="cg-subnav-helper__field cg-subnav-helper__field--link" readonly value="#' + escapeAttr(item.anchor) + '" data-tip="Click to select, Ctrl/Cmd+C to copy" />';
          html += '</li>';
        });
        html += '</ul>';
      }

      panel.innerHTML = html;
      this.el.parentNode.insertBefore(panel, this.el);

      panel.addEventListener('focus', function (e) {
        var input = e.target.closest('.cg-subnav-helper__field');
        if (!input) return;
        input.select();
      }, true);
      panel.addEventListener('click', function (e) {
        var input = e.target.closest('.cg-subnav-helper__field');
        if (!input) return;
        input.select();
      });
    }

    scanCandidates() {
      var sections = document.querySelectorAll('.shopify-section');
      var seen = new Set();
      var out = [];
      sections.forEach(function (section) {
        if (!section.id || seen.has(section.id)) return;
        if (section.closest('.cg-subnav')) return;
        var classes = section.className || '';
        if (classes.indexOf('shopify-section-group-header-group') !== -1) return;
        if (classes.indexOf('shopify-section-group-footer-group') !== -1) return;
        if (section.offsetHeight < 200) return;

        // Prefer h1/h2 text. Fallback: derive a label from the section's
        // trailing id segment (so headless sections like "__hero" still
        // appear in the helper as "Hero" with anchor "#hero").
        var label = '';
        var heading = section.querySelector('h1, h2');
        if (heading) {
          label = (heading.textContent || '').trim().replace(/\s+/g, ' ');
        }
        var idTail = section.id.split('__').pop().replace(/[-_]+/g, ' ').trim();
        var anchor = section.id.split('__').pop();
        if (!label) {
          if (!idTail) return;
          label = idTail.replace(/\b\w/g, function (c) { return c.toUpperCase(); });
        } else {
          // Suggest the section's id-tail as the anchor — short, stable
          // across template duplications, and resolved by the JS suffix-match.
          if (!anchor) {
            anchor = label.toLowerCase()
              .replace(/[^a-z0-9]+/g, '-')
              .replace(/^-+|-+$/g, '')
              .slice(0, 30);
          }
        }
        seen.add(section.id);
        out.push({ label: label, anchor: anchor || section.id });
      });
      return out;
    }

    // ------------------------------------------------------------------
    // Discovery — read rendered <li> items, map in-page anchors to targets
    // ------------------------------------------------------------------
    mapLinksToTargets() {
      var self = this;
      this.links.forEach(function (a) {
        var href = a.getAttribute('href') || '';
        if (!href || href.charAt(0) !== '#') return;       // external/page link → no scroll-spy
        var id = href.slice(1);
        if (!id) return;

        // If the href looks like a stale Shopify section id
        // (shopify-section-template--XXX__name), extract just the trailing
        // "name" so we can suffix-match against the page's current sections.
        var parts = id.split('__');
        var shortName = parts[parts.length - 1];

        var target = null;
        try { target = document.getElementById(id); } catch (e) {}

        // Suffix match by short name (handles both stale full-ids and the
        // friendly anchors the helper now suggests, e.g. `#science`).
        if (!target) {
          try {
            var bySuffix = document.querySelectorAll(
              '[id$="__' + shortName + '"], [id$="-' + shortName + '"], [id="' + shortName + '"]'
            );
            // Prefer a real section element over any other match.
            for (var k = 0; k < bySuffix.length; k++) {
              if (bySuffix[k].classList && bySuffix[k].classList.contains('shopify-section')) {
                target = bySuffix[k];
                break;
              }
            }
            if (!target && bySuffix.length >= 1) target = bySuffix[0];
          } catch (e) {}
        }

        // Final fallback: scan section headings for a matching label.
        if (!target) {
          var lower = shortName.toLowerCase().replace(/[-_]/g, ' ').trim();
          var sections = document.querySelectorAll('.shopify-section');
          for (var i = 0; i < sections.length; i++) {
            var h = sections[i].querySelector('h1, h2');
            if (!h) continue;
            var ht = (h.textContent || '').trim().toLowerCase();
            if (ht && (ht === lower || ht.indexOf(lower) === 0)) {
              target = sections[i];
              break;
            }
          }
        }

        if (!target) return;
        var resolvedId = target.id || id;
        if (!target.id) target.id = id;
        target.setAttribute(ANCHOR_TARGET_ATTR, resolvedId);
        a.setAttribute('href', '#' + (target.id || id));
        self.items.push({ li: a.parentElement, a: a, target: target, anchor: target.id || id });
      });
    }

    // ------------------------------------------------------------------
    // Scroll spy
    // ------------------------------------------------------------------
    initScrollSpy() {
      var self = this;
      // BUG-05 / J016: match the reference rootMargin exactly — only ONE
      // section occupies the 20-30% band at a time, no aggressive pinball.
      var options = {
        root: null,
        rootMargin: '-20% 0px -70% 0px',
        threshold: 0
      };

      this.observer = new IntersectionObserver(function (entries) {
        // Click-lock window: keep the clicked pill active during smooth-scroll.
        if (self.lockedTarget && Date.now() < self.lockedUntil) {
          self.setActive(self.lockedTarget);
          self.el.classList.remove('cg-subnav--hide');
          return;
        }
        var best = null;
        var bestRatio = 0;
        for (var i = 0; i < entries.length; i++) {
          var entry = entries[i];
          if (!entry.isIntersecting) continue;
          var vis = self.visibility(entry.boundingClientRect);
          if (vis > bestRatio) {
            bestRatio = vis;
            best = entry.target;
          }
        }
        if (best) {
          self.setActive(best);
          self.el.classList.remove('cg-subnav--hide');
        } else {
          self.fallbackSpy();
        }
      }, options);

      for (var i = 0; i < this.items.length; i++) {
        this.observer.observe(this.items[i].target);
      }

      this.scrollHandler = function () {
        if (self.rafToken) cancelAnimationFrame(self.rafToken);
        self.rafToken = requestAnimationFrame(function () {
          self.rafToken = null;
          self.fallbackSpy();
        });
      };
      window.addEventListener('scroll', this.scrollHandler, { passive: true });

      // Seed a first pass now so something is active on load.
      this.fallbackSpy();
    }

    teardownSpy() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
      if (this.scrollHandler) {
        window.removeEventListener('scroll', this.scrollHandler);
        this.scrollHandler = null;
      }
      if (this.rafToken) {
        cancelAnimationFrame(this.rafToken);
        this.rafToken = null;
      }
    }

    visibility(rect) {
      var vh = window.innerHeight || document.documentElement.clientHeight;
      var top = Math.max(0, rect.top);
      var bottom = Math.min(vh, rect.bottom);
      return Math.max(0, bottom - top) / Math.max(rect.height, 1);
    }

    isAtBottom() {
      var scrollY = window.pageYOffset || document.documentElement.scrollTop;
      var vh = window.innerHeight || document.documentElement.clientHeight;
      var docHeight = Math.max(
        document.body.scrollHeight,
        document.documentElement.scrollHeight,
        document.body.offsetHeight,
        document.documentElement.offsetHeight
      );
      return (scrollY + vh) >= (docHeight - 20);
    }

    /**
     * Gumshoe-style reverse-iterate fallback.
     * Walks targets last -> first; the first one whose top is at or above
     * the trigger line wins. Bottom-of-page override forces the last item.
     */
    fallbackSpy() {
      if (!this.items.length) return;

      // Click-lock window: don't override the user's clicked pill while
      // the smooth-scroll is still moving toward its destination.
      if (this.lockedTarget && Date.now() < this.lockedUntil) {
        this.setActive(this.lockedTarget);
        this.el.classList.remove('cg-subnav--hide');
        return;
      }

      var scrollY = window.pageYOffset || document.documentElement.scrollTop;
      var vh = window.innerHeight || document.documentElement.clientHeight;
      // BUG-05 / J028: reference uses `scrollY + 0.1 * vh` (the 10%-from-top line).
      var triggerLine = scrollY + (vh * 0.1);

      // Bottom-of-page: last target wins (useLastItem pattern).
      if (this.isAtBottom()) {
        var lastItem = this.items[this.items.length - 1];
        this.setActive(lastItem.target);
        this.el.classList.remove('cg-subnav--hide');
        return;
      }

      var activeIdx = -1;
      for (var i = this.items.length - 1; i >= 0; i--) {
        var rect = this.items[i].target.getBoundingClientRect();
        var top = rect.top + scrollY;
        if (top <= triggerLine) {
          activeIdx = i;
          break;
        }
      }

      if (activeIdx >= 0) {
        this.setActive(this.items[activeIdx].target);
        this.el.classList.remove('cg-subnav--hide');
      } else {
        // Above the first target — no active item, but keep nav visible.
        this.clearActive();
        var last = this.items[this.items.length - 1];
        var lastBottom = last.target.getBoundingClientRect().bottom + scrollY;
        if (scrollY > lastBottom) {
          this.el.classList.add('cg-subnav--hide');
        } else {
          this.el.classList.remove('cg-subnav--hide');
        }
      }
    }

    setActive(targetEl) {
      if (this.activeEl === targetEl) return;
      this.activeEl = targetEl;
      for (var i = 0; i < this.items.length; i++) {
        var item = this.items[i];
        var isActive = item.target === targetEl;
        item.li.classList.toggle('cg-subnav__item--active', isActive);
        if (isActive) {
          item.a.setAttribute('aria-current', 'true');
        } else {
          item.a.removeAttribute('aria-current');
        }
      }
    }

    clearActive() {
      this.activeEl = null;
      for (var i = 0; i < this.items.length; i++) {
        this.items[i].li.classList.remove('cg-subnav__item--active');
        this.items[i].a.removeAttribute('aria-current');
      }
    }

    // ------------------------------------------------------------------
    // Mobile toggler + auto-collapse on link click
    // ------------------------------------------------------------------
    initToggler() {
      if (!this.toggler) return;
      var self = this;
      this.togglerHandler = function (e) {
        e.preventDefault();
        // BUG-15: toggler tap is also user-initiated navigation.
        self.el.classList.add('cg-subnav--user-navigated');
        var expanded = self.el.classList.toggle('cg-subnav--expanded');
        self.toggler.classList.toggle('cg-subnav__toggler--active', expanded);
        self.toggler.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      };
      this.toggler.addEventListener('click', this.togglerHandler);
    }

    initClickCollapse() {
      // Clear any old handlers before reattaching.
      for (var j = 0; j < this.linkHandlers.length; j++) {
        var h = this.linkHandlers[j];
        h.a.removeEventListener('click', h.fn);
      }
      this.linkHandlers = [];

      var self = this;
      this.links.forEach(function (a) {
        var fn = function () {
          // BUG-15: mark user-initiated nav. The mobile collapse-to-single-pill
          // layout is gated behind this so it never fires from pure scroll-spy.
          self.el.classList.add('cg-subnav--user-navigated');
          self.el.classList.remove('cg-subnav--expanded');
          if (self.toggler) {
            self.toggler.classList.remove('cg-subnav__toggler--active');
            self.toggler.setAttribute('aria-expanded', 'false');
          }
          // Highlight the clicked pill immediately and lock it for ~900ms
          // so scroll-spy can't override it during the smooth-scroll
          // animation (the destination section isn't in the spy's trigger
          // band yet, so otherwise it'd flip back to the previous section).
          for (var i = 0; i < self.items.length; i++) {
            if (self.items[i].a === a) {
              self.lockedTarget = self.items[i].target;
              self.lockedUntil = Date.now() + 900;
              self.setActive(self.items[i].target);
              break;
            }
          }
        };
        a.addEventListener('click', fn);
        self.linkHandlers.push({ a: a, fn: fn });
      });
    }

    // ------------------------------------------------------------------
    // Cleanup
    // ------------------------------------------------------------------
    destroy() {
      this.teardownSpy();
      if (this.toggler && this.togglerHandler) {
        this.toggler.removeEventListener('click', this.togglerHandler);
        this.togglerHandler = null;
      }
      for (var i = 0; i < this.linkHandlers.length; i++) {
        var h = this.linkHandlers[i];
        h.a.removeEventListener('click', h.fn);
      }
      this.linkHandlers = [];
      for (var j = 0; j < this.items.length; j++) {
        this.items[j].target.removeAttribute(ANCHOR_TARGET_ATTR);
      }
      var helper = this.el.parentNode && this.el.parentNode.querySelector('[data-cg-subnav-helper]');
      if (helper) helper.remove();
      if (this.stickyTrackHandler) {
        window.removeEventListener('scroll', this.stickyTrackHandler);
        window.removeEventListener('resize', this.stickyTrackHandler);
        this.stickyTrackHandler = null;
      }
    }
  }

  // --------------------------------------------------------------------
  // Init / Shopify theme-editor wiring
  // --------------------------------------------------------------------
  function init() {
    document.querySelectorAll(SELECTOR).forEach(function (el) {
      if (!el.__cgSubnav) {
        el.__cgSubnav = new CgSubnav(el);
      }
    });
  }

  function rebuildAll() {
    document.querySelectorAll(SELECTOR).forEach(function (el) {
      if (el.__cgSubnav) {
        el.__cgSubnav.destroy();
        el.__cgSubnav = null;
      }
      el.__cgSubnav = new CgSubnav(el);
    });
  }

  // Run init early (DOMContentLoaded) so the subnav's sticky top is set
  // from the real header position before the user sees the page. Re-run
  // on window.load to pick up any late-laid-out images that may have
  // changed section heights for the spy.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  window.addEventListener('load', init);

  // Theme editor + dynamic section reloads.
  document.addEventListener('shopify:section:load', function () {
    init();
    rebuildAll();
  });
  document.addEventListener('shopify:section:unload', rebuildAll);
  document.addEventListener('shopify:section:reorder', rebuildAll);
})();
